// Finding an evidence excerpt on a page the browser rendered itself.
//
// A span's offsets are into the text the model read — pdftotext or Tesseract
// output on the server — and mean nothing in pdf.js's text layer, which lays
// the same page out in its own items. What does carry over is the characters.
// The worker records them beside the span (0031), and this finds them among the
// text layer's items.
//
// Whitespace is ignored on both sides: `pdftotext -layout` pads columns with
// runs of spaces and pdf.js splits items wherever the PDF does, so the spacing
// of the two readings never agrees, and the non-space characters always do.
//
// The answer is a location only when it is unambiguous. An excerpt that occurs
// twice ("21.00" as both a line's VAT and the VAT total) could be either, and
// marking one of them would be marking a guess with complete conviction — so
// it is `ambiguous`, nothing is highlighted, and the screen says so.

export type EvidenceLocation =
  | { readonly kind: 'located'; readonly page: number; readonly items: readonly number[] }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'ambiguous' };

/** Shorter than this, after removing whitespace, and nothing is located. */
export const MIN_EVIDENCE_CHARACTERS = 2;

const SPACE = /\s+/g;

/**
 * Locate `excerpt` among `pages` (each a list of text-layer item strings). With
 * `page` given, only that 1-based page is searched; without, every page is,
 * and the excerpt must occur exactly once across all of them.
 */
export function locateEvidence(
  pages: readonly (readonly string[])[],
  excerpt: string,
  page: number | null,
): EvidenceLocation {
  const needle = excerpt.replace(SPACE, '');
  if (needle.length < MIN_EVIDENCE_CHARACTERS) return { kind: 'not_found' };

  const scope = page === null ? pages.map((_, index) => index) : [page - 1];
  let found: { page: number; items: number[] } | null = null;

  for (const index of scope) {
    const items = pages[index];
    if (items === undefined) continue;
    // The page's characters with whitespace removed, and which item each came from.
    let haystack = '';
    const owner: number[] = [];
    items.forEach((item, itemIndex) => {
      const stripped = item.replace(SPACE, '');
      haystack += stripped;
      for (let i = 0; i < stripped.length; i += 1) owner.push(itemIndex);
    });

    let from = 0;
    for (;;) {
      const at = haystack.indexOf(needle, from);
      if (at === -1) break;
      if (found !== null) return { kind: 'ambiguous' };
      const covered = new Set<number>();
      for (let i = at; i < at + needle.length; i += 1) covered.add(owner[i] as number);
      found = { page: index + 1, items: [...covered].sort((a, b) => a - b) };
      from = at + 1;
    }
  }
  return found === null ? { kind: 'not_found' } : { kind: 'located', ...found };
}
